/**
 * MachineCommand.js
 * Machine + Feeder ekleme/silme komutları
 *
 * Machine eklenirken:
 * 1. Component (machine) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine machine ve wire eklenir
 *
 * ⚠️ NOT: MachineCommand ayrıca wireSlice kullanır (addWire/deleteWire)
 */

import BaseWiringCommand from './BaseWiringCommand';
import * as THREE from 'three';
import { createMachineSymbol } from '../../../factory/symbols/createMachines';
import { addWire, deleteWire } from '../../../redux/wireSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';

class MachineCommand extends BaseWiringCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} component - Machine component verisi
   * @param {object} feeder - Feeder verisi
   * @param {object} options - Ek opsiyonlar
   */
  constructor(action, component, feeder, options = {}) {
    // Accept options in two shapes: either { machineSymbolData: {...} }
    // or the legacy form where options === sceneData. Normalize here.
    const machineSymbolData = (options && options.machineSymbolData) ? options.machineSymbolData : options;
    const sceneData = {
      ...machineSymbolData,
      wireVertices: feeder.vertices || []
    };

    // Determine chosen color for this command. Prefer explicit sceneData.color,
    // then feeder metadata, then fall back to legacy blue.
    const chosenColor = (sceneData && sceneData.color) || (feeder && feeder.metadata && feeder.metadata.color) || 0x2233ff;

    super(
      action === 'ADD' ? 'ADD_MACHINE' : 'DELETE_MACHINE',
      action,
      component,
      feeder,
      sceneData,
      {
        componentType: 'machine',
        cacheMethodSuffix: 'Machine',
        wireColor: chosenColor,
        createWire: true
      }
    );

    // MachineCommand'a özgü özellikler
    this.wireId = options.wireId || generateUniqueId('wireMachine');
    this.restoreVertices = options.restoreVertices || [];
    this.restoreCallback = options.restoreCallback;
    // optional restoreSource or panel id passed by caller
    this.restoreSource = options.restoreSource || null;
    this.branchID = options.branchID || (component && component.metadata && component.metadata.branchId) || generateUniqueId('branchMachineCmd');
  }

  /**
   * Machine sembolünü oluştur
   * @override
   */
  createSymbol(scene, component, sceneData) {
    // ✅ DUPLICATE DETECTION: Aynı ID ile machine zaten varsa yeniden oluşturma
    const machineId = component?.id;
    if (machineId && scene) {
      let existingMachine = null;
      scene.traverse(obj => {
        if (obj.userData?.id === machineId || obj.userData?.componentId === machineId) {
          existingMachine = obj;
        }
      });
      if (existingMachine) {
        return existingMachine;
      }
    }

    // Extract data from sceneData - handle both array and object formats
    const machinePosition = sceneData.machinePosition || sceneData.position;
    const flipDirection = sceneData.dir || sceneData.flipDirection;
    const machineType = sceneData.machineType;
    const power = sceneData.power;
    const angle = sceneData.angle ?? sceneData.rotation ?? sceneData.rotateValue ?? 0;

    

    if (!machinePosition) {
      console.error('❌ MachineCommand.createSymbol: machinePosition is null/undefined!');
      return null;
    }

    // Convert position array to object if needed
    const position = Array.isArray(machinePosition)
      ? { x: machinePosition[0], y: machinePosition[1], z: machinePosition[2] || 0 }
      : machinePosition;

    // Ensure flipDirection exists and has valid values
    const direction = flipDirection ? new THREE.Vector3(flipDirection.x, flipDirection.y, flipDirection.z || 0) : new THREE.Vector3(1, 0, 0);

    const symbolColor = this.config && this.config.wireColor ? this.config.wireColor : 0xff3400;

    // Offset position by 25 units in direction (machine center offset from wire endpoint)
    // createMachineSymbol handles the offset internally if direction is Vector3
    // so we pass the base position
    
    const machineSymbol = createMachineSymbol(
        null,
        position,
        direction,
        symbolColor,
        false,
        machineType,
        power,
        angle
      );

    

    if (!machineSymbol) {
      console.error('❌ createMachineSymbol returned null!');
      return null;
    }

    machineSymbol.name = 'machineSymbol';

    // Rotation set et
    if (machineSymbol.children?.[0]) {
      machineSymbol.children[0].rotation.z = angle;
    }

    // userData ekstra alanlar
    machineSymbol.userData = {
      ...machineSymbol.userData,
      machineType,
      power,
      componentId: component.id
    };

    return machineSymbol;
  }

  /**
   * Wire çizgisi oluştur - MachineCommand'a özgü override
   * @override
   */
  createWire(feederData, sceneData, componentData) {
    const wireVertices = sceneData.wireVertices || feederData.vertices;

    if (!Array.isArray(wireVertices) || wireVertices.length < 2) {
      return null;
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(
      wireVertices.map(v => new THREE.Vector3(v.x, v.y, v.z))
    );
    const material = new THREE.LineBasicMaterial({ color: this.config.wireColor });
    const wireLine = new THREE.Line(geometry, material);

    wireLine.name = 'machineWire';

    // ✅ Machine ID'yi al - componentData öncelikli, sonra this.data
    const componentId = componentData?.id || this.data.after?.component?.id || this.data.before?.component?.id;

    wireLine.userData = {
      id: this.wireId,
      feederId: feederData?.id, // ✅ Redux feeder ID eşleştirmesi için
      type: 'polyline',
      layer: 'machine',
      isSelectable: true,
      componentId: componentId,
      branchID: feederData.metadata?.branchId || this.data.after?.component?.metadata?.branchId,
      panelID: feederData.from,
      // ✅ Gizmo için machine ID'leri ekle
      machineIds: componentId ? [componentId] : [],
      machineIdsMap: componentId ? { [componentId]: true } : {}
    };

    return wireLine;
  }

  /**
   * Execute - wireSlice işlemleri için override
   * @override
   */
  execute(scene, dispatch, skipRedux = false) {
    // Base execute'u çağır
    super.execute(scene, dispatch, skipRedux);

    // wireSlice'a ekle (skipRedux=false ise)
    if (!skipRedux) {
      const feederData = this.data.after?.feeder || this.data.before?.feeder;
      const componentData = this.data.after?.component || this.data.before?.component;

      if (this.action === 'ADD' && feederData.vertices) {
        // serialize configured wire color to hex for storage in wireSlice
        const hexColor = '#' + ((this.config && this.config.wireColor) ? (this.config.wireColor >>> 0).toString(16).padStart(6, '0') : '2233ff');

          dispatch(
            addWire({
              id: this.wireId,
              sceneUUID: this.wireLine?.uuid,
              type: 'polyline',
              vertices: feederData.vertices,
              layer: 'machine',
              color: hexColor,
              userData: {
                selectable: true,
                parent: 'machine',
                panelID: feederData.from,
                branchID: componentData.metadata?.branchId
              }
            })
          );
      }
    }

    // Wire marker oluşturma için feederData'yı dışarda tanımla
    const feederData = this.data.after?.feeder || this.data.before?.feeder;

    // Panel/out metadata: try to add out info into the panel's userData outs
    try {
      const branchId = this.branchID;
      const panelId = feederData.from || this.sceneData?.panelID || (this.restoreSource && this.restoreSource.panelID) || null;
      if (panelId && scene) {
        const panelObj = scene.getObjectByProperty ? scene.getObjectByProperty('uuid', panelId) : null;
        // fallback traversal
        const foundPanel = panelObj || (panelId && scene && (() => { let f=null; scene.traverse(o=>{ if (o.userData?.id===panelId||o.uuid===panelId) f=o; }); return f; })());
        if (foundPanel) {
          foundPanel.userData = foundPanel.userData || {};
          foundPanel.userData.outs = foundPanel.userData.outs || [];
          // push a branch entry if not already present
          const exists = foundPanel.userData.outs.some(o => o.branchID === branchId);
          if (!exists) {
            foundPanel.userData.outs.push({
              branchID: branchId,
              power: this.data.after?.component?.metadata?.power || 0,
              branchType: 'machine',
              description: this.data.after?.component?.metadata?.machineType || this.data.after?.component?.name || 'machine',
              length: (feederData && feederData.vertices) ? feederData.vertices.reduce((acc, v, i, arr) => { if (i===0) return 0; const a=arr[i-1]; return acc + Math.hypot(v.x-a.x, v.y-a.y); }, 0).toFixed(2) : '0',
              mcbReference: 213,
              wires: [{ id: this.wireId, length: (feederData && feederData.vertices) ? (feederData.vertices.reduce((acc, v, i, arr) => { if (i===0) return 0; const a=arr[i-1]; return acc + Math.hypot(v.x-a.x, v.y-a.y); }, 0).toFixed(2)) : '0', machine: this.data.after?.component, power: this.data.after?.component?.electrical?.installedPower }]
            });
          }
        }
      }
    } catch (err) {
      // non-fatal
      // console.warn('MachineCommand: panel out add failed', err);
    }

    if (this.action === 'ADD' && feederData.vertices) {

      // ✅ KALICI WIRE MARKER'LAR EKLE (150 birimden büyük segmentler için)
      // 3-phase machine için 3 çizgili marker (socket hattı gibi)
      const wireVertices = feederData.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z));

      const rotateVector = (vector, angleRad) => {
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);
        return new THREE.Vector3(
          vector.x * cos - vector.y * sin,
          vector.x * sin + vector.y * cos,
          0
        );
      };

      const wireMarkerIds = [];
      const wireMarkerIdsMap = {};

      // İlk edge'i atla (panel çıkışı)
      for (let i = 1; i < wireVertices.length - 1; i++) {
        const p1 = wireVertices[i];
        const p2 = wireVertices[i + 1];
        const dir = new THREE.Vector3().subVectors(p2, p1);
        const length = dir.length();

        if (length < 150) continue;

        const normal = dir.clone().normalize();
        const angleRad = THREE.MathUtils.degToRad(45); // 45 derece açı
        const angledDir = rotateVector(normal, angleRad); // 45° döndürülmüş yön
        const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

        const forwardOffset = normal.clone().setLength(3); // Kenar yönünde 3 birim
        const halfLength = 2.5;

        // Kenarın açısını hesapla
        const edgeAngle = Math.atan2(dir.y, dir.x);

        // ✅ 3 merkez nokta: kenar yönünde offset'li (preview ile aynı)
        const positions = [
          mid.clone().sub(forwardOffset),  // Geri
          mid.clone(),                      // Orta
          mid.clone().add(forwardOffset),   // İleri
        ];

        // ✅ Markerları Grup Olarak Oluştur (Yapısal bütünlük için)
        const markerGroup = new THREE.Group();
        markerGroup.position.set(0, 0, 0); // ✅ Dünya koordinatlarında çizgiler
        markerGroup.userData = {
          role: 'wireMarker',
          parentWireId: this.wireId,
          type: 'permanent',
          id: markerGroup.uuid, // ✅ Gizmo için ID
          edgeIndex: i, // ✅ Hangi kenarın ortasında (updateGizmos için)
          lastEdgeAngle: edgeAngle + angleRad, // ✅ updateGizmos için başlangıç açısı
          midPoint: mid.clone() // ✅ Kenarın orta noktası (gizmo güncellemeleri için)
        };

        // ✅ Her merkez noktada angledDir yönünde çizgi oluştur (preview ile aynı)
        positions.forEach((centerPoint) => {
          // Çizgi: centerPoint ± angledDir * halfLength (dünya koordinatları)
          const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
          const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));

          const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
          const mat = new THREE.LineBasicMaterial({ color: 0xffffff });
          const markerLine = new THREE.Line(geo, mat);
          markerGroup.add(markerLine);
        });

        scene.add(markerGroup);
        wireMarkerIds.push(markerGroup.uuid);
        wireMarkerIdsMap[markerGroup.uuid] = true;
      }

      // ✅ WireLine userData güncelle
      if (this.wireLine) {
        this.wireLine.userData.wireMarkerIds = wireMarkerIds;
        this.wireLine.userData.wireMarkerIdsMap = wireMarkerIdsMap;
      }
    }
  }

  /**
   * Undo - wireSlice işlemleri + restoreCallback için override
   * @override
   */
  undo(scene, dispatch, skipRedux = false) {
    // Base undo'yu çağır
     super.undo(scene, dispatch, skipRedux);

    // wireSlice'tan sil (skipRedux=false ise)
    if (!skipRedux && this.wireId) {
      dispatch(deleteWire(this.wireId));
    }

    // ✅ Wire marker'ları temizle
    if (scene && this.wireId) {
      const markersToRemove = [];
      scene.traverse((obj) => {
        if (obj.userData?.role === 'wireMarker' && obj.userData?.parentWireId === this.wireId) {
          markersToRemove.push(obj);
        }
      });

      markersToRemove.forEach((marker) => {
        scene.remove(marker);
        if (marker.traverse) {
          marker.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });
        }
        if (marker.geometry) marker.geometry.dispose();
        if (marker.material) marker.material.dispose();
      });
    }

    // ✅ Restore callback: re-enter draw mode with vertices and machine info
    if (this.restoreCallback) {
      try {
        // ✅ isRedoContext = false (UNDO) - useBaseWiring'de exit point eklenmemeli
        this.restoreCallback(this.restoreVertices, this.restoreSource, false, this.sceneData);
      } catch (err) {
        console.warn('7-MachineCommand.undo: restoreCallback threw', err);
      }
    }

    // Remove panel out entry added during execute
    try {
      const branchId = this.branchID;
      const feederData = this.data.after?.feeder || this.data.before?.feeder;
      const panelId = feederData.from || this.sceneData?.panelID || (this.restoreSource && this.restoreSource.panelID) || null;
      if (panelId && scene) {
        const panelObj = scene.getObjectByProperty ? scene.getObjectByProperty('uuid', panelId) : null;
        const foundPanel = panelObj || (panelId && scene && (() => { let f=null; scene.traverse(o=>{ if (o.userData?.id===panelId||o.uuid===panelId) f=o; }); return f; })());
        if (foundPanel && foundPanel.userData && Array.isArray(foundPanel.userData.outs)) {
          foundPanel.userData.outs = foundPanel.userData.outs.filter(o => o.branchID !== branchId);
        }
      }
    } catch (err) {
      // ignore
    }
  }

  /**
   * JSON'dan MachineCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_MACHINE' ? 'ADD' : 'DELETE';
    const { component, feeder } = json.data.after || json.data.before;

    return new MachineCommand(action, component, feeder, {
      machineSymbolData: json.sceneData,
      wireId: json.wireId,
      restoreVertices: json.restoreVertices || []
    });
  }

  /**
   * Include restoreVertices and wireId in serialized output so
   * transaction snapshots and operation history contain the
   * wire vertex data necessary to restore the drawing state.
   */
  toJSON() {
    const base = super.toJSON();
    return {
      ...base,
      wireId: this.wireId,
      restoreVertices: this.restoreVertices || []
    };
  }
}

export default MachineCommand;
